import kioskRequest from '../../kioskRequest';
import kioskAdminRequest from '../../kioskAdminRequest';
import { kiosk, survey } from '../../endpoints';
import { Config } from '../../../utils/Config';
import axios from 'axios';

export default class kioskApi {
  /**
   * Get school information by slug
   * @param {string} schoolSlug - School slug
   * @returns {Promise} School info with theme, URN info, etc.
   */
  static async getSchoolBySlug(schoolSlug) {
    return kioskRequest({
      url: kiosk.getSchoolInfo(schoolSlug),
      method: 'GET',
    });
  }

  /**
   * Verify URN for school
   * @param {string} schoolSlug - School slug
   * @param {string} urn - URN to verify
   * @returns {Promise} Verification result
   */
  static async verifyURN(schoolSlug, urn) {
    return kioskRequest({
      url: kiosk.verifyURN(schoolSlug),
      method: 'POST',
      data: JSON.stringify({ urn }),
    });
  }

  /**
   * Search students by name
   * @param {string} schoolSlug - School slug
   * @param {string} query - Search query (minimum 3 characters)
   * @returns {Promise} Array of students
   */
  static async searchStudents(schoolSlug, query) {
    if (!query || query.length < 3) {
      return Promise.resolve([]);
    }
    return kioskRequest({
      url: kiosk.searchStudents(schoolSlug, query),
      method: 'GET',
    });
  }

  /**
   * Search parents by child name
   * @param {string} schoolSlug - School slug
   * @param {string} query - Search query (minimum 3 characters)
   * @returns {Promise} Array of parents/children
   */
  static async searchParents(schoolSlug, query) {
    if (!query || query.length < 3) {
      return Promise.resolve([]);
    }
    return kioskRequest({
      url: kiosk.searchParents(schoolSlug, query),
      method: 'GET',
    });
  }

  /**
   * Verify student by DOB
   * @param {number} studentId - Student ID
   * @param {string} dateOfBirth - Date of birth in YYYY-MM-DD format
   * @returns {Promise} Verification result with student info
   */
  static async verifyStudent(studentId, dateOfBirth) {
    return kioskRequest({
      url: kiosk.verifyStudent(),
      method: 'POST',
      data: JSON.stringify({ studentId, dateOfBirth }),
    });
  }

  /**
   * Verify parent by child DOB
   * @param {number} parentId - Parent ID
   * @param {string} childDateOfBirth - Child date of birth in YYYY-MM-DD format
   * @returns {Promise} Verification result with parent and children info
   */
  static async verifyParent(parentId, childDateOfBirth) {
    return kioskRequest({
      url: kiosk.verifyParent(),
      method: 'POST',
      data: JSON.stringify({ parentId, childDateOfBirth }),
    });
  }

  /**
   * Get surveys for student using Insights (Custom API) only
   * Uses existing getPupilSurveys endpoint from Insights API.
   * Do NOT rely on PeopleManagement /Kiosk .../surveys endpoints.
   * @param {number} schoolId - School ID (from school info)
   * @param {number} studentId - Student ID
   * @returns {Promise} Array of available surveys
   */
  static async getStudentSurveys(schoolId, studentId) {
    try {
      // Call Insights API endpoint directly (unauthenticated)
      const insightsClient = axios.create({
        responseType: 'json',
        baseURL: Config.BASE_URL,
        transformResponse: (response) => response,
      });
      
      insightsClient.defaults.headers.common['Content-Type'] = 'application/json';
      insightsClient.defaults.headers.common.Accept = 'application/json';
      insightsClient.defaults.withCredentials = false;
      insightsClient.defaults.timeout = 240000;

      const response = await insightsClient({
        url: survey.getPupilSurveys(schoolId, studentId),
        method: 'GET',
      });

      if (typeof response.data === 'string') {
        return JSON.parse(response.data);
      }
      return response.data || [];
    } catch (err) {
      console.error('Error fetching student surveys:', err);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get surveys for parent using Insights (Custom API)
   * Uses new GetParentSurveys endpoint from Insights API (staging/live).
   * @param {number} schoolId - School ID (from school info)
   * @param {number} parentId - Parent ID
   * @returns {Promise} Array of available surveys
   */
  static async getParentSurveys(schoolId, parentId) {
    try {
      // Call Insights parent-surveys endpoint directly (unauthenticated)
      const insightsClient = axios.create({
        responseType: 'json',
        baseURL: Config.BASE_URL,
        transformResponse: (response) => response,
      });
      
      insightsClient.defaults.headers.common['Content-Type'] = 'application/json';
      insightsClient.defaults.headers.common.Accept = 'application/json';
      insightsClient.defaults.withCredentials = false;
      insightsClient.defaults.timeout = 240000;

      try {
        const response = await insightsClient({
          url: `/survey/GetParentSurveys?schoolId=${schoolId}&parentId=${parentId}`,
          method: 'GET',
        });

        if (typeof response.data === 'string') {
          return JSON.parse(response.data);
        }
        return response.data || [];
      } catch (insightsErr) {
        // Parent surveys endpoint doesn't exist in Insights API yet
        console.warn('Parent surveys endpoint not available in Insights API');
        return [];
      }
    } catch (err) {
      console.error('Error fetching parent surveys:', err);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get kiosk admin settings
   * @param {number} schoolId - School ID
   * @returns {Promise} Kiosk settings
   */
  static async getKioskSettings(schoolId) {
    // /KioskAdmin/* requires the admin's bearer token (WelbeeAuthorize + scope guard).
    return kioskAdminRequest({
      url: kiosk.getKioskSettings(schoolId),
      method: 'GET',
    });
  }

  /**
   * Get kiosk status
   * @param {number} schoolId - School ID
   * @returns {Promise} Kiosk status with current mode
   */
  static async getKioskStatus(schoolId) {
    return kioskAdminRequest({
      url: kiosk.getKioskStatus(schoolId),
      method: 'GET',
    });
  }

  /**
   * Set kiosk mode (runtime selection on the public kiosk device — no auth token)
   * @param {string} schoolSlug - School slug
   * @param {string} mode - Mode: 'Parent' or 'Student'
   * @returns {Promise} Updated settings
   */
  static async setKioskMode(schoolSlug, mode) {
    return kioskRequest({
      url: kiosk.setKioskMode(schoolSlug),
      method: 'POST',
      data: JSON.stringify({ mode }),
    });
  }

  /**
   * Exit kiosk mode (runtime, on the public kiosk device — no auth token)
   * @param {string} schoolSlug - School slug
   * @returns {Promise} Success message
   */
  static async exitKiosk(schoolSlug) {
    return kioskRequest({
      url: kiosk.exitKiosk(schoolSlug),
      method: 'POST',
    });
  }
}
